import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';
import { Pype } from '../xstate/prostudio.api-service.types';
import { checkForErrors } from './check-for-errors';
import { getCustomerPypes } from './get-customer-pypes';

/**
 * `autoresponders` lives on the `Pype` GraphQL type, not on `V6CustomersInfo`
 * (what GetCustomerPypesV6 returns), so it can't be added to that query.
 * Fetched here via the separate Pype-returning bulk endpoint and merged in by
 * id — same "one bulk request, not one per pype" reasoning as
 * load-error-handling.ts.
 */
export const getDefaultMessagesPypes = async ({
  uniqueCustomerId,
  projectId,
  botId,
}: {
  uniqueCustomerId: string;
  projectId: string;
  /**
   * Scopes the result to the selected solution. Default Messages lives inside a
   * solution, so without this the page listed every pype on the customer —
   * including other solutions' channels and the Live Agent Platform connectors.
   * The autoresponders request below is customer-wide (that endpoint takes no
   * bot filter), but its values are merged by id, so filtering the pype list is
   * what decides the rows.
   */
  botId?: string;
}): Promise<{ live: Pype[]; sandbox: Pype[] }> => {
  const [liveAgentPypes, autorespondersResponse] = await Promise.all([
    getCustomerPypes({ uniqueCustomerId, projectId, botId }),
    urqlGql.getCustomersPypesAutoresponders({
      customerId: uniqueCustomerId,
      projectId,
    }),
  ]);

  checkForErrors(autorespondersResponse);

  const autorespondersById = new Map(
    [
      ...(autorespondersResponse.data?.pypesGetCustomersPypes ?? []),
      ...(autorespondersResponse.data?.pypesGetCustomersPypesSandbox ?? []),
    ].map((pype) => [pype.id, pype.autoresponders])
  );

  const withAutoresponders = (pypes: Pype[]): Pype[] =>
    pypes.map((pype) => ({
      ...pype,
      autoresponders: (pype.id && autorespondersById.get(pype.id)) || null,
    }));

  return {
    live: withAutoresponders(liveAgentPypes.live),
    sandbox: withAutoresponders(liveAgentPypes.sandbox),
  };
};
